import { Select, Tooltip } from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { buildStatusOptions } from '../utils/state-options';

const EMAIL_SEARCH_TOOLTIP = 'You cannot use the status filter when searching by email address.';

type StatusFilterProps = {
  value: string | null;
  /** Available channels — the status options are the union across them. */
  channels: readonly string[];
  /** Selected channel (`''` = all). */
  actionType: string;
  isDisabled: boolean;
  /** In email-search mode the filter is disabled and explains why on hover. */
  searchByEmail: boolean;
  onChange: (value: string | null) => void;
};

/**
 * The grouped delivery/user-action status select. Disabled while searching by
 * email address, with a custom tooltip explaining why. Ported from the Ember
 * status `PlumaSelect` + its `PlumaTooltip`.
 */
export function StatusFilter({ value, channels, actionType, isDisabled, searchByEmail, onChange }: StatusFilterProps) {
  const selectProps = {
    ariaLabel: 'Filter by status',
    placeholder: 'Status',
    value: isEmptyString(value) ? undefined : value,
    options: buildStatusOptions(channels, actionType),
    onChange,
    isClearable: true,
    isDisabled,
    size: 'sm',
  } as const;

  if (!searchByEmail) return <Select {...selectProps} />;

  // The tooltip anchors to the Select's own field wrapper: pointer events fire on
  // it even though the underlying button is disabled, so no extra wrapper is needed.
  return (
    <Tooltip content={EMAIL_SEARCH_TOOLTIP} isCustom placement="top">
      {(context) => (
        <Select {...selectProps} ref={context.reference.setReference} {...context.reference.getReferenceProps()} />
      )}
    </Tooltip>
  );
}
